import { useRef, useEffect, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { TILE_SIZE, FLOOR_HEIGHT } from './constants';
import type { Position } from '@/types/game';

interface FloatingTextEntry {
  id: number;
  position: Position;
  value: number;
}

interface FloatingText3DProps {
  totalCols: number;
  totalRows: number;
}

function FloatingTextItem({ entry, totalCols, totalRows }: { entry: FloatingTextEntry; totalCols: number; totalRows: number }) {
  const groupRef = useRef<THREE.Group>(null);
  const elapsed = useRef(0);

  const x = (entry.position[0] - (totalCols - 1) / 2) * TILE_SIZE;
  const z = (entry.position[1] - (totalRows - 1) / 2) * TILE_SIZE;
  const baseY = FLOOR_HEIGHT + 0.6;

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    elapsed.current += delta;
    groupRef.current.position.y = baseY + elapsed.current * 1.2;
  });

  const opacity = 1;

  return (
    <group ref={groupRef} position={[x, baseY, z]}>
      <Html center style={{ pointerEvents: 'none' }}>
        <div
          style={{
            color: '#FFD700',
            fontSize: 18,
            fontWeight: 'bold',
            fontFamily: 'monospace',
            textShadow: '0 2px 4px rgba(0,0,0,0.8)',
            opacity,
            whiteSpace: 'nowrap',
          }}
        >
          +{entry.value}
        </div>
      </Html>
    </group>
  );
}

export default function FloatingText3D({ totalCols, totalRows }: FloatingText3DProps) {
  const [texts, setTexts] = useState<FloatingTextEntry[]>([]);
  const idRef = useRef(0);

  useEffect(() => {
    const handleCoinCollected = (event: Event) => {
      const { position, value } = (event as CustomEvent).detail;
      const id = idRef.current++;
      setTexts(prev => [...prev, { id, position, value }]);
      setTimeout(() => {
        setTexts(prev => prev.filter(t => t.id !== id));
      }, 800);
    };

    window.addEventListener('coinCollected', handleCoinCollected);
    return () => window.removeEventListener('coinCollected', handleCoinCollected);
  }, []);

  return (
    <>
      {texts.map((entry) => (
        <FloatingTextItem
          key={entry.id}
          entry={entry}
          totalCols={totalCols}
          totalRows={totalRows}
        />
      ))}
    </>
  );
}
